import React, { useState } from 'react';
import { Mail, Clock, MessageCircle, Phone } from 'lucide-react';
import { ContactButtons } from './ContactButtons';
import { useLanguage, Language } from '../contexts/LanguageContext';

// El formulario envía a la Edge Function contact-message (Supabase): guarda el mensaje y avisa a info@.
const ENDPOINT = 'https://jcbxwtuhshcyzlhpikxr.supabase.co/functions/v1/contact-message';
const ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpjYnh3dHVoc2hjeXpsaHBpa3hyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA0NDc5MzAsImV4cCI6MjEwNjAyMzkzMH0.TCCTiwKNauedwdX3R0_Ag0MCZId2nZGmuLIN86FcH-U';
type ContactStrings = {
  heading: string;
  heading2: string;
  subtitle: string;
  subtitle2: string;
  subtitle3: string;
  callTitle: string;
  callBody: string;
  emailTitle: string;
  emailBody: string;
  multiTitle: string;
  multiBody: string;
  hoursTitle: string;
  hoursBody1: string;
  hoursBody2: string;
  formTitle: string;
  formSubtitle: string;
  firstName: string;
  lastName: string;
  email: string;
  language: string;
  message: string;
  firstNamePh: string;
  lastNamePh: string;
  emailPh: string;
  messagePh: string;
  sendBtn: string;
  sending: string;
  sentTitle: string;
  sentBody: string;
  errMsg: string;
  missing: string;
  privacyNote: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
const T: Record<Language, ContactStrings> = {
  en: {
    heading: 'Need help?',
    heading2: "We're here for you, any time.",
    subtitle: 'Reach out in your preferred language, day or night.',
    subtitle2: 'The fastest way is to call or message us on WhatsApp.',
    subtitle3: 'Asking which form you need is always free.',
    callTitle: 'Call or message us — 24/7',
    callBody: 'Call any hour, day or night — our multilingual virtual assistant always answers and will help you in your language. Asking which form you need is free.',
    emailTitle: 'Email Support',
    emailBody: 'We respond within 24 hours.',
    multiTitle: 'Multilingual Help',
    multiBody: "Reach us in English, Spanish, Haitian Creole, or Vietnamese — we'll help you in the same language.",
    hoursTitle: 'Open 24/7',
    hoursBody1: 'Fill out your form online anytime — day or night, any day of the week.',
    hoursBody2: 'Our virtual assistant answers calls around the clock to guide you in your language.',
    formTitle: 'Send us a message',
    formSubtitle: "We'll get back to you within one business day.",
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    language: 'Preferred Language',
    message: 'Message',
    firstNamePh: 'John',
    lastNamePh: 'Doe',
    emailPh: 'john@example.com',
    messagePh: 'How can we help you?',
    sendBtn: 'Send Message',
    sending: 'Sending…',
    sentTitle: 'Message sent — thank you!',
    sentBody: "We received your message and will reply to your email within one business day.",
    errMsg: 'We could not send your message. Please try again, or email us at info@noorpassportservices.com.',
    missing: 'Please enter your name, a valid email, and your message.',
    privacyNote: 'Please do not share sensitive document numbers (passport, Social Security) by email.'
  },
  es: {
    heading: '¿Necesita ayuda?',
    heading2: 'Estamos para usted a cualquier hora',
    subtitle: 'Comuníquese en el idioma que prefiera, de día o de noche.',
    subtitle2: 'La forma más rápida es llamarnos o escribirnos por WhatsApp.',
    subtitle3: 'Preguntar cuál formulario necesita siempre es gratis.',
    callTitle: 'Llámenos o escríbanos — 24/7',
    callBody: 'Llame a cualquier hora, de día o de noche — nuestro asistente virtual multilingüe siempre contesta y le ayuda en su idioma. Preguntar cuál formulario necesita es gratis.',
    emailTitle: 'Soporte por correo',
    emailBody: 'Respondemos en menos de 24 horas.',
    multiTitle: 'Ayuda multilingüe',
    multiBody: 'Comuníquese en inglés, español, creole haitiano o vietnamita — le ayudamos en el mismo idioma.',
    hoursTitle: 'Abierto 24/7',
    hoursBody1: 'Llene su formulario en línea a cualquier hora — de día o de noche, cualquier día de la semana.',
    hoursBody2: 'Nuestro asistente virtual contesta llamadas a toda hora para guiarle en su idioma.',
    formTitle: 'Envíenos un mensaje',
    formSubtitle: 'Le responderemos en un día hábil.',
    firstName: 'Nombre',
    lastName: 'Apellido',
    email: 'Correo electrónico',
    language: 'Idioma preferido',
    message: 'Mensaje',
    firstNamePh: 'Juan',
    lastNamePh: 'Pérez',
    emailPh: 'juan@ejemplo.com',
    messagePh: '¿Cómo podemos ayudarle?',
    sendBtn: 'Enviar mensaje',
    sending: 'Enviando…',
    sentTitle: 'Mensaje enviado — ¡gracias!',
    sentBody: 'Recibimos su mensaje y le responderemos a su correo en un día hábil.',
    errMsg: 'No pudimos enviar su mensaje. Intente de nuevo, o escríbanos a info@noorpassportservices.com.',
    missing: 'Escriba su nombre, un correo válido y su mensaje.',
    privacyNote: 'Por favor no comparta números de documentos delicados (pasaporte, Seguro Social) por correo.'
  },
  ht: {
    heading: 'Ou bezwen èd?',
    heading2: 'Nou la pou ou nenpòt lè',
    subtitle: 'Kontakte nou nan lang ou pi pito, lajounen kou lannwit.',
    subtitle2: 'Fason ki pi rapid se rele oswa voye mesaj ban nou sou WhatsApp.',
    subtitle3: 'Mande ki fòm ou bezwen toujou gratis.',
    callTitle: 'Rele oswa voye mesaj ban nou — 24/7',
    callBody: 'Rele nenpòt lè, lajounen kou lannwit — asistan vityèl miltileng nou an toujou reponn epi l ap ede ou nan lang ou. Mande ki fòm ou bezwen gratis.',
    emailTitle: 'Sipò pa imèl',
    emailBody: 'Nou reponn nan mwens pase 24 èdtan.',
    multiTitle: 'Èd nan plizyè lang',
    multiBody: 'Kontakte nou an anglè, pannyòl, kreyòl ayisyen, oswa vyetnamyen — n ap ede ou nan menm lang lan.',
    hoursTitle: 'Louvri 24/7',
    hoursBody1: 'Ranpli fòm ou sou entènèt nenpòt lè — lajounen kou lannwit, nenpòt jou nan semèn nan.',
    hoursBody2: 'Asistan vityèl nou an reponn apèl yo tout lè pou gide ou nan lang ou.',
    formTitle: 'Voye nou yon mesaj',
    formSubtitle: 'N ap reponn ou nan yon jou ouvrab.',
    firstName: 'Prenon',
    lastName: 'Siyati',
    email: 'Adrès imèl',
    language: 'Lang ou pi pito',
    message: 'Mesaj',
    firstNamePh: 'Jan',
    lastNamePh: 'Pyè',
    emailPh: 'jan@egzanp.com',
    messagePh: 'Kijan nou ka ede ou?',
    sendBtn: 'Voye mesaj la',
    sending: 'N ap voye…',
    sentTitle: 'Mesaj la voye — mesi!',
    sentBody: 'Nou resevwa mesaj ou a epi n ap reponn ou nan imèl ou nan yon jou ouvrab.',
    errMsg: 'Nou pa t kapab voye mesaj ou a. Tanpri eseye ankò, oswa ekri nou nan info@noorpassportservices.com.',
    missing: 'Tanpri ekri non ou, yon imèl ki valab, ak mesaj ou.',
    privacyNote: 'Tanpri pa pataje nimewo dokiman sansib (paspò, Sekirite Sosyal) pa imèl.'
  },
  vi: {
    heading: 'Cần trợ giúp?',
    heading2: 'Chúng tôi luôn sẵn sàng, bất cứ lúc nào',
    subtitle: 'Hãy liên hệ bằng ngôn ngữ quý vị muốn, ngày hay đêm.',
    subtitle2: 'Cách nhanh nhất là gọi hoặc nhắn tin cho chúng tôi qua WhatsApp.',
    subtitle3: 'Hỏi quý vị cần mẫu đơn nào luôn miễn phí.',
    callTitle: 'Gọi hoặc nhắn tin cho chúng tôi — 24/7',
    callBody: 'Gọi bất cứ giờ nào, ngày hay đêm — trợ lý ảo đa ngôn ngữ của chúng tôi luôn trả lời và sẽ giúp quý vị bằng ngôn ngữ của mình. Hỏi quý vị cần mẫu đơn nào là miễn phí.',
    emailTitle: 'Hỗ trợ qua email',
    emailBody: 'Chúng tôi phản hồi trong vòng 24 giờ.',
    multiTitle: 'Hỗ trợ đa ngôn ngữ',
    multiBody: 'Liên hệ với chúng tôi bằng tiếng Anh, Tây Ban Nha, Creole Haiti hoặc tiếng Việt — chúng tôi sẽ giúp quý vị bằng chính ngôn ngữ đó.',
    hoursTitle: 'Mở cửa 24/7',
    hoursBody1: 'Điền mẫu đơn của quý vị trực tuyến bất cứ lúc nào — ngày hay đêm, bất kỳ ngày nào trong tuần.',
    hoursBody2: 'Trợ lý ảo của chúng tôi trả lời cuộc gọi suốt ngày đêm để hướng dẫn quý vị bằng ngôn ngữ của mình.',
    formTitle: 'Gửi tin nhắn cho chúng tôi',
    formSubtitle: 'Chúng tôi sẽ phản hồi trong vòng một ngày làm việc.',
    firstName: 'Tên',
    lastName: 'Họ',
    email: 'Địa chỉ email',
    language: 'Ngôn ngữ ưu tiên',
    message: 'Tin nhắn',
    firstNamePh: 'Văn',
    lastNamePh: 'Nguyễn',
    emailPh: 'vannguyen@vidu.com',
    messagePh: 'Chúng tôi có thể giúp gì cho quý vị?',
    sendBtn: 'Gửi tin nhắn',
    sending: 'Đang gửi…',
    sentTitle: 'Đã gửi tin nhắn — cảm ơn quý vị!',
    sentBody: 'Chúng tôi đã nhận được tin nhắn và sẽ trả lời qua email của quý vị trong vòng một ngày làm việc.',
    errMsg: 'Chúng tôi không thể gửi tin nhắn. Xin thử lại, hoặc gửi email cho chúng tôi tại info@noorpassportservices.com.',
    missing: 'Vui lòng nhập tên, email hợp lệ và tin nhắn của quý vị.',
    privacyNote: 'Vui lòng không chia sẻ số giấy tờ nhạy cảm (hộ chiếu, An sinh Xã hội) qua email.'
  }
};
const inputCls = 'w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white min-h-[48px]';
export function ContactSection() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [lang, setLang] = useState<string>(language);
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // trampa anti-robots (debe quedar vacío)
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!firstName.trim() && !lastName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || message.trim().length < 2) {
      setErr(t.missing);
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON,
          Authorization: `Bearer ${ANON}`
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          lang,
          message: message.trim(),
          website
        })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error('send');
      setSent(true);
    } catch {
      setErr(t.errMsg);
    } finally {
      setBusy(false);
    }
  };
  return <section className="py-24 bg-white relative z-10 border-t border-brand-border" data-id="element-90" data-mp="13d7fy6volmkn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-91" data-mp="20rjpath8yw2q">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-92" data-mp="20twe9qlfa6pa">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-93" data-mp="1w01wwttai3td">
            {t.heading}
            <span className="block" data-id="element-94" data-mp="23dj8h4ujif2w">{t.heading2}</span>
          </h2>
          <p className="text-lg text-brand-textSecondary" data-id="element-95" data-mp="28asx4ia3umpo">
            {t.subtitle}
            <span className="block" data-id="element-96" data-mp="2dap1wjqmmi6a">{t.subtitle2}</span>
            <span className="block" data-id="element-97" data-mp="2d946krwhmv7d">{t.subtitle3}</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start" data-id="element-98" data-mp="2ia9zapq5eigw">
          {/* Contact details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-99" data-mp="2makgyznayxbl">
            {/* Call / WhatsApp — primary, fastest channel */}
            <div className="bg-white rounded-2xl p-6 border-2 border-brand-accent shadow-premium" data-id="element-100" data-mp="2r83w01rtre4l">
              <div className="flex items-center gap-2 mb-1" data-id="element-101" data-mp="2w2zzpz6fbpxj">
                <Phone className="w-5 h-5 text-brand-accent" aria-hidden="true" data-id="element-102" data-mp="30yyj18cwahr3" />
                <h3 className="text-lg font-semibold text-brand-textPrimary" data-id="element-103" data-mp="35zmh2dzjjyut">
                  {t.callTitle}
                </h3>
              </div>
              <p className="text-brand-textSecondary text-sm mb-4" data-id="element-104" data-mp="39yll7wdg7rl7">
                {t.callBody}
              </p>
              <ContactButtons size="lg" showNumber className="flex flex-col items-stretch" data-id="element-105" data-mp="1atk2hp7dlpfk" />
            </div>

            <div className="bg-brand-bg/40 rounded-2xl p-6 border border-brand-border" data-id="element-106" data-mp="1jpjcpii4ukx2">
              <div className="flex items-start gap-4" data-id="element-107" data-mp="1evynofyip1a0">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-brand-border" data-id="element-108" data-mp="1tm5xk7aglbz3">
                  <Mail className="w-5 h-5 text-brand-accent" data-id="element-109" data-mp="1omjlw2aa43se" />
                </div>
                <div data-id="element-110" data-mp="22m2s2pjj5522">
                  <h3 className="text-lg font-semibold text-brand-textPrimary mb-1" data-id="element-111" data-mp="22mkmdgmmt97c">
                    {t.emailTitle}
                  </h3>
                  <p className="text-brand-textSecondary text-sm mb-2" data-id="element-112" data-mp="88td4i87srcf">
                    {t.emailBody}
                  </p>
                  <a href="mailto:info@noorpassportservices.com" className="text-brand-secondaryLink hover:text-brand-textPrimary font-medium transition-colors break-all underline underline-offset-2" data-id="element-113" data-mp="48j9hc7wad0q">
                    info@noorpassportservices.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-brand-bg/40 rounded-2xl p-6 border border-brand-border" data-id="element-114" data-mp="3lhfbrg8vknqd">
              <div className="flex items-start gap-4" data-id="element-115" data-mp="36kg8g4l23h1p">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-brand-border" data-id="element-116" data-mp="36n2e1yodzmha">
                  <MessageCircle className="w-5 h-5 text-brand-accent" data-id="element-117" data-mp="2xkt3j8t4bt3w" />
                </div>
                <div data-id="element-118" data-mp="32j2ybiaxkroz">
                  <h3 className="text-lg font-semibold text-brand-textPrimary mb-1" data-id="element-119" data-mp="32ke39lg445ho">
                    {t.multiTitle}
                  </h3>
                  <p className="text-brand-textSecondary text-sm" data-id="element-120" data-mp="vqtt0pntlfs9">
                    {t.multiBody}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-brand-bg/40 rounded-2xl p-6 border border-brand-border" data-id="element-121" data-mp="2wumbgqz5scwi">
              <div className="flex items-start gap-4" data-id="element-122" data-mp="2rxcmtdjlg59q">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-brand-border" data-id="element-123" data-mp="2mwoo05p4ik4y">
                  <Clock className="w-5 h-5 text-brand-accent" data-id="element-124" data-mp="2iul9t9b2l1fz" />
                </div>
                <div data-id="element-125" data-mp="2dxbmbz78qzus">
                  <h3 className="text-lg font-semibold text-brand-textPrimary mb-1" data-id="element-126" data-mp="2dxusyjrfy1ti">
                    {t.hoursTitle}
                  </h3>
                  <p className="text-brand-textSecondary text-sm" data-id="element-127" data-mp="24679b514lp2l">
                    {t.hoursBody1}
                  </p>
                  <p className="text-brand-textSecondary text-sm mt-1" data-id="element-128" data-mp="1z85ghsdwebdx">
                    {t.hoursBody2}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-brand-bg/40 rounded-3xl p-8 md:p-10 border border-brand-border shadow-sm" data-id="element-129" data-mp="1q77l1yhizc6y">
            <h3 className="text-2xl font-bold text-brand-textPrimary mb-2" data-id="element-130" data-mp="5qrcordl7xvm">
              {t.formTitle}
            </h3>
            <p className="text-brand-textSecondary mb-8" data-id="element-131" data-mp="epbnl6cwp39a">{t.formSubtitle}</p>

            {sent ? <div role="status" className="rounded-2xl bg-white border border-brand-accent p-8 text-center" data-id="element-132" data-mp="jofufqccyowg">
                <p className="text-xl font-bold text-brand-textPrimary mb-2" data-id="element-133" data-mp="3j3t9f19ib3ba">{t.sentTitle}</p>
                <p className="text-brand-textSecondary" data-id="element-134" data-mp="3n4vcxuagi1j2">{t.sentBody}</p>
              </div> : <form className="space-y-5" onSubmit={submit} noValidate data-id="element-135" data-mp="3s3ptepkybe00">
                <div className="grid sm:grid-cols-2 gap-5" data-id="element-136" data-mp="3s2eup46d4wfr">
                  <div data-id="element-137" data-mp="r4yofpxqdlr">
                    <label htmlFor="firstName" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-138" data-mp="rn4o42eg66v">
                      {t.firstName}
                    </label>
                    <input type="text" id="firstName" value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" className={inputCls} placeholder={t.firstNamePh} data-id="element-139" data-mp="bjdhlkeigstu" />
                  </div>
                  <div data-id="element-140" data-mp="3oq6un3e1ukcv">
                    <label htmlFor="lastName" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-141" data-mp="3orhwgxnxpi1c">
                      {t.lastName}
                    </label>
                    <input type="text" id="lastName" value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" className={inputCls} placeholder={t.lastNamePh} data-id="element-142" data-mp="1jmnhjtv4fua" />
                  </div>
                </div>

                <div data-id="element-143" data-mp="1kkost0sdp2ra">
                  <label htmlFor="email" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-144" data-mp="1kkx9l3rmgs7r">
                    {t.email}
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" className={inputCls} placeholder={t.emailPh} data-id="element-145" data-mp="1bks5b2hq69xt" />
                </div>

                <div data-id="element-146" data-mp="1uil721gm4dtm">
                  <label htmlFor="language" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-147" data-mp="1ui33ejrmexbo">
                    {t.language}
                  </label>
                  <select id="language" value={lang} onChange={e => setLang(e.target.value)} className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white min-h-[48px] appearance-none bg-no-repeat bg-right pr-10" style={{
                backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%235F6B7A' d='M5.5 7.5L10 12l4.5-4.5'/%3E%3C/svg%3E\")",
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25rem'
              }} data-id="element-148" data-mp="2n0gp5kzomsue">
                    <option value="en" data-id="element-149" data-mp="3hl1gycf4ebm0">English</option>
                    <option value="es" data-id="element-150" data-mp="3hmw2nsy7u7r5">Español</option>
                    <option value="ht" data-id="element-151" data-mp="3clow1x7t7em2">Kreyòl Ayisyen</option>
                    <option value="vi" data-id="element-152" data-mp="3cob3zxym3w4t">Tiếng Việt</option>
                  </select>
                </div>

                <div data-id="element-153" data-mp="1s2cnsw8gr1jn">
                  <label htmlFor="message" className="block text-base font-medium text-brand-textPrimary mb-2" data-id="element-154" data-mp="1x30ns7eo6sq0">
                    {t.message}
                  </label>
                  <textarea id="message" rows={5} value={message} onChange={e => setMessage(e.target.value)} maxLength={4000} className="w-full px-4 py-3.5 text-base rounded-xl border border-brand-border focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all bg-white resize-none" placeholder={t.messagePh} data-id="element-155" data-mp="25wm2jqqu49zf"></textarea>
                </div>

                {/* Trampa anti-robots: invisible para personas */}
                <div style={{
              position: 'absolute',
              left: '-9999px'
            }} aria-hidden="true" data-id="element-156" data-mp="uh6wzykk89sp">
                  <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} data-id="element-157" data-mp="eaqaz9ytjmpc" />
                </div>

                {err && <p role="alert" className="text-red-700 font-medium" data-id="element-158" data-mp="szmrzwiusa23">
                    {err}
                  </p>}

                <button type="submit" disabled={busy} className="w-full bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-6 py-4 rounded-full font-semibold text-lg transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 min-h-[52px] disabled:opacity-60" data-id="element-159" data-mp="xzqz4vxvfsfn">
                  {busy ? t.sending : t.sendBtn}
                </button>

                <p className="text-xs text-brand-textSecondary text-center pt-2" data-id="element-160" data-mp="3dp2ggkulnmok">
                  {t.privacyNote}
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}